'use client';

/**
 * AIBriefingRail — 320px right-rail companion surfacing Astor's curated briefing
 * for the current scope. Mounted on Home and reusable wherever an at-a-glance
 * rail is appropriate. Sections (top → bottom): header, today's snapshot (2×2
 * MetricCell grid), Astor's read (mint-glow card + chips), latest updates
 * (channel-keyed list + ghost "view all" footer). Empty state collapses all
 * three body sections into a single muted caption.
 *
 * Width is fixed at 320 with `flexShrink: 0` — caller is responsible for placing
 * the rail inside a flex layout next to the main content column.
 *
 * @see /tmp/design-fetch/fiops/project/pages/home-astor.jsx (lines 162–225)
 */

import SparkleIcon from '@mui/icons-material/AutoAwesome';
import BoltOutlined from '@mui/icons-material/BoltOutlined';
import CloseIcon from '@mui/icons-material/Close';
import FlagOutlined from '@mui/icons-material/FlagOutlined';
import FolderOutlined from '@mui/icons-material/FolderOutlined';
import KeyboardArrowDownOutlined from '@mui/icons-material/KeyboardArrowDownOutlined';
import MailOutlineOutlined from '@mui/icons-material/MailOutlineOutlined';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import { alpha, type SxProps, type Theme, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import type { ReactNode } from 'react';
import React from 'react';
import { useI18n } from '../../hooks/useI18n';
import { iconSizes } from '../../theme/typography';
import { composeSx } from '../../utils/styledUtils';
import { GlassButton } from '../GlassButton';
import { GlassChip } from '../GlassChip';
import { GlassIconButton, GlassIconButtonSize } from '../GlassIconButton';
import { MetricCell } from '../MetricCell';
import { AIBriefingUpdateRow } from './AIBriefingUpdateRow';

// ============================================================================
// Types
// ============================================================================

export interface AIBriefingSnapshot {
  /** Active loans — value + signed weekly delta (`'+2'` / `'-1'` / `'0'`). */
  activeLoans: { value: number; deltaSigned: string };
  /** SLA-risk — count + percent (rendered to 1 decimal as `"0.0%"`). */
  slaRisk: { value: number; percent: number };
  /** Unread messages — count + optional already-formatted "cleared at" clock. */
  unreadMessages: { value: number; clearedAt?: string };
  /** Priority signals — count + how many are new since last view. */
  prioritySignals: { value: number; newCount: number };
}

export interface AIBriefingRead {
  /** Pre-rendered narrative (caller can inline `<strong>`, emphasis spans, etc.). */
  narrative: ReactNode;
  /** Astor's confidence (0–100). */
  confidencePercent: number;
  /** Estimated read time, seconds. */
  readSeconds: number;
}

export type AIBriefingUpdateChannel = 'EMAIL' | 'IN_APP' | 'SECURITY' | 'AUDIT';

export interface AIBriefingUpdate {
  id: string;
  channel: AIBriefingUpdateChannel;
  /** Pre-localized title — this component does NOT translate update titles. */
  title: string;
  /** Pre-formatted relative time, e.g. `"8m"`, `"1h"`. */
  relativeTime: string;
  onClick?: () => void;
}

export interface AIBriefingRailProps {
  /** When omitted, the snapshot section is hidden. */
  snapshot?: AIBriefingSnapshot;
  /** When omitted, the Astor's-read card is hidden. */
  read?: AIBriefingRead;
  /** Latest-updates list. Empty array hides the section. */
  updates: AIBriefingUpdate[];
  /** Fires when the user dismisses the rail via the close button. */
  onCollapse?: () => void;
  /** Fires when the user clicks the chevron (caller wires the scope/date menu). */
  onScopeClick?: () => void;
  /** When provided, the "View all updates →" footer button is rendered. */
  onViewAllUpdates?: () => void;
  /** Custom empty-state node; defaults to the `aiBriefing.empty` caption. */
  emptyState?: ReactNode;
  /** Sx overrides applied to the root <aside>. */
  sx?: SxProps<Theme>;
}

// ============================================================================
// Constants & helpers
// ============================================================================

const RAIL_WIDTH = 320;

function SectionHeader({ children }: { children: ReactNode }) {
  return (
    <Typography
      variant="overline"
      sx={{ display: 'block', color: 'text.secondary', fontWeight: 600, letterSpacing: '0.08em', mb: 1 }}
    >
      {children}
    </Typography>
  );
}

// ============================================================================
// Main component
// ============================================================================

export function AIBriefingRail({
  snapshot,
  read,
  updates,
  onCollapse,
  onScopeClick,
  onViewAllUpdates,
  emptyState,
  sx,
}: AIBriefingRailProps): React.JSX.Element {
  const theme = useTheme();
  const { t } = useI18n();
  const isDark = theme.palette.mode === 'dark';
  const isEmpty = snapshot == null && read == null && updates.length === 0;

  // Mint-glow card — mirrors StageMapDrawer / BriefViewPopover sibling-shipped style.
  const mintGlowBg = alpha(theme.palette.success.main, isDark ? 0.1 : 0.08);
  const mintGlowBorder = alpha(theme.palette.success.main, 0.38);

  // Snapshot tiles as data — keeps the JSX flat.
  const snapshotTiles = snapshot
    ? ([
        {
          key: 'activeLoans',
          label: t('aiBriefing.snapshot.activeLoans'),
          value: snapshot.activeLoans.value,
          helper: t('aiBriefing.snapshot.activeLoansSub', { deltaSigned: snapshot.activeLoans.deltaSigned }),
          Icon: FolderOutlined,
          color: 'info.main',
        },
        {
          key: 'slaRisk',
          label: t('aiBriefing.snapshot.slaRisk'),
          value: snapshot.slaRisk.value,
          helper: t('aiBriefing.snapshot.slaRiskSubPercent', { percent: snapshot.slaRisk.percent.toFixed(1) }),
          Icon: FlagOutlined,
          color: 'success.main',
        },
        {
          key: 'unreadMessages',
          label: t('aiBriefing.snapshot.unreadMessages'),
          value: snapshot.unreadMessages.value,
          helper: snapshot.unreadMessages.clearedAt
            ? t('aiBriefing.snapshot.unreadMessagesSub', { time: snapshot.unreadMessages.clearedAt })
            : undefined,
          Icon: MailOutlineOutlined,
          color: 'secondary.main',
        },
        {
          key: 'prioritySignals',
          label: t('aiBriefing.snapshot.prioritySignals'),
          value: snapshot.prioritySignals.value,
          helper: t('aiBriefing.snapshot.prioritySignalsSub', { newCount: snapshot.prioritySignals.newCount }),
          Icon: BoltOutlined,
          color: 'warning.main',
        },
      ] as const)
    : [];

  return (
    <Paper
      component="aside"
      role="complementary"
      aria-label={t('aiBriefing.title')}
      elevation={0}
      sx={composeSx(
        {
          width: RAIL_WIDTH,
          flexShrink: 0,
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          p: 2,
          borderLeft: '1px solid',
          borderColor: 'divider',
          bgcolor: 'background.paper',
          borderRadius: 0,
        },
        sx,
      )}
      data-testid="ai-briefing-rail"
    >
      {/* Header */}
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, minWidth: 0 }}>
          <SparkleIcon aria-hidden sx={{ fontSize: iconSizes.sm, color: 'success.main' }} />
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 700,
              color: 'text.primary',
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              whiteSpace: 'nowrap',
            }}
          >
            {t('aiBriefing.title')}
          </Typography>
          {onScopeClick != null ? (
            <GlassIconButton
              size={GlassIconButtonSize.Small}
              variant="subtle"
              onClick={onScopeClick}
              aria-haspopup="menu"
              aria-label={t('aiBriefing.title')}
              sx={{ color: 'text.secondary' }}
            >
              <KeyboardArrowDownOutlined sx={{ fontSize: iconSizes.xs }} />
            </GlassIconButton>
          ) : (
            <KeyboardArrowDownOutlined aria-hidden sx={{ fontSize: iconSizes.xs, color: 'text.secondary' }} />
          )}
        </Box>
        {onCollapse != null && (
          <GlassIconButton
            size={GlassIconButtonSize.Medium}
            variant="subtle"
            onClick={onCollapse}
            aria-label={t('aiBriefing.collapse')}
            sx={{ color: 'text.secondary' }}
          >
            <CloseIcon sx={{ fontSize: iconSizes.sm }} />
          </GlassIconButton>
        )}
      </Box>

      {isEmpty ? (
        <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', py: 4 }}>
          {emptyState ?? (
            <Typography
              variant="caption"
              sx={{ color: 'text.secondary', textAlign: 'center', maxWidth: 240, lineHeight: 1.5 }}
            >
              {t('aiBriefing.empty')}
            </Typography>
          )}
        </Box>
      ) : (
        <>
          {snapshot && (
            <Box>
              <SectionHeader>{t('aiBriefing.snapshot.header')}</SectionHeader>
              <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', columnGap: 1.5, rowGap: 1.5 }}>
                {snapshotTiles.map(({ key, label, value, helper, Icon, color }) => (
                  <MetricCell
                    key={key}
                    label={label}
                    value={value}
                    helper={helper}
                    endAdornment={<Icon aria-hidden sx={{ fontSize: iconSizes.xs, color }} />}
                  />
                ))}
              </Box>
            </Box>
          )}

          {read && (
            <Box>
              <SectionHeader>{t('aiBriefing.read.header')}</SectionHeader>
              <Box
                sx={{
                  bgcolor: mintGlowBg,
                  border: '1px solid',
                  borderColor: mintGlowBorder,
                  borderRadius: 1.25,
                  p: 1.5,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 1,
                }}
              >
                <Typography variant="body2" sx={{ color: 'text.primary', lineHeight: 1.55 }}>
                  {read.narrative}
                </Typography>
                <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
                  <GlassChip
                    size="small"
                    color="success"
                    label={t('aiBriefing.read.confidence', { percent: read.confidencePercent })}
                  />
                  <GlassChip
                    size="small"
                    variant="outlined"
                    label={t('aiBriefing.read.readTime', { seconds: read.readSeconds })}
                    sx={{ fontVariantNumeric: 'tabular-nums' }}
                  />
                </Box>
              </Box>
            </Box>
          )}

          {updates.length > 0 && (
            <Box>
              <SectionHeader>{t('aiBriefing.updates.header')}</SectionHeader>
              <Box sx={{ display: 'flex', flexDirection: 'column' }}>
                {updates.map((update, index) => (
                  <AIBriefingUpdateRow
                    key={update.id}
                    update={update}
                    showDivider={index < updates.length - 1}
                    channelLabel={t('aiBriefing.updates.channel', { channel: update.channel })}
                  />
                ))}
              </Box>
              {onViewAllUpdates && (
                <Box sx={{ pt: 1 }}>
                  <GlassButton
                    variant="ghost"
                    color="info"
                    onClick={onViewAllUpdates}
                    sx={{ px: 0, justifyContent: 'flex-start', minHeight: 44 }}
                  >
                    {t('aiBriefing.updates.viewAll')} {'→'}
                  </GlassButton>
                </Box>
              )}
            </Box>
          )}
        </>
      )}
    </Paper>
  );
}

AIBriefingRail.displayName = 'AIBriefingRail';

export default AIBriefingRail;
